// Who has a paid plan, for the marking credit meters: the student's own plan, a parent's ("sponsor") plan
// assigned to the student, or membership of an active school (school students get the same access as paid users,
// Pavlo + Mike, 5 Oct 2026). Order: own > sponsor > school. One rule for the server credit gate and the client meters;
// the sponsor half matches production's checkAndGetSubscriptionFromConnectedParent (src/firebase/customers-ts.ts).
// Pure and import-free on purpose: node --test runs it with type stripping (src/utils/paid-access.test.mjs).

export interface PaidAccessSubscription {
  status?: string
  role?: string | null
  metadata?: Record<string, string> | null
}

// A school from users/{studentId}.connectedSchools, read from schools/{id}.
export interface PaidAccessSchool {
  id: string
  schoolName?: string
  isSuspended?: boolean
  isDisabled?: boolean
}

export interface PaidAccessInput {
  studentId: string
  ownSubscriptions: PaidAccessSubscription[]
  // Present when users/{studentId}.sponsorId is set. connections: those with studentEmail == the student's email;
  // sponsorSubscriptions: customers/{sponsorId}/subscriptions.
  sponsor?: {
    sponsorId: string
    connections: { status?: string; parentId?: string }[]
    sponsorSubscriptions: PaidAccessSubscription[]
  } | null
  // The existing school docs behind users/{studentId}.connectedSchools.
  schools?: PaidAccessSchool[] | null
}

export interface PaidAccess {
  paid: boolean
  source: 'own' | 'sponsor' | 'school' | null
  // Set when source is 'school', for the "Unlimited via <school>" badge.
  schoolName?: string
}

const TUTOR_PLAN_ROLES = ['graduateTutor', 'qualifiedTeacher', 'examiner']
// metadataSubscriptionAssignmentKeyPrefix in src/constants/subscriptions.ts, repeated to keep this file import-free.
const ASSIGNMENT_KEY_PREFIX = 'assignedTo'

// A plan role (Stripe product metadata.firebaseRole) that counts as paid. Also decides 'Paid' in Mautic
// (src/utils/mautic/deriveSubscriptionStatus.ts), so the app and the marketing segments agree.
export const isPaidPlanRole = (role: string | null | undefined): boolean =>
  !!role && role !== 'free' && !TUTOR_PLAN_ROLES.includes(role)

// Subscription statuses that keep the plan's access on. past_due: Stripe Smart Retries are retrying a failed
// renewal, and the webhook moves the subscription to Free only after the last attempt (#3770). unpaid, canceled
// and incomplete give no access. Every status check for access should go through this list.
export const ACCESS_STATUSES = ['active', 'trialing', 'past_due']

export const isAccessStatus = (status: string | null | undefined): boolean =>
  !!status && ACCESS_STATUSES.includes(status)

export const isPaidSubscription = (
  subscription: PaidAccessSubscription,
): boolean =>
  isAccessStatus(subscription.status) && isPaidPlanRole(subscription.role)

export const isActiveSchool = (school: PaidAccessSchool): boolean =>
  !school.isSuspended && !school.isDisabled

// users.connectedSchools -> the school ids to read. Tolerates a missing, null or non-list field (seen in data) and
// caps the reads; a student normally has one school.
export const MAX_SCHOOLS_READ = 5
export const schoolIdsToRead = (connectedSchools: unknown): string[] =>
  Array.isArray(connectedSchools)
    ? [
        ...new Set(
          connectedSchools.filter(
            (id): id is string => typeof id === 'string' && id !== '',
          ),
        ),
      ].slice(0, MAX_SCHOOLS_READ)
    : []

// schools/{id} data -> the fields the rule needs.
export const toPaidAccessSchool = (
  id: string,
  data: Record<string, unknown>,
): PaidAccessSchool => ({
  id,
  schoolName:
    typeof data.schoolName === 'string' ? data.schoolName : undefined,
  isSuspended: data.isSuspended === true,
  isDisabled: data.isDisabled === true,
})

function hasSponsorAccess(
  studentId: string,
  sponsor: PaidAccessInput['sponsor'],
): boolean {
  if (!sponsor?.sponsorId) return false

  const approved = sponsor.connections.some(
    (connection) =>
      connection.status === 'approved' &&
      connection.parentId === sponsor.sponsorId,
  )
  const assignmentKey = `${ASSIGNMENT_KEY_PREFIX}_${studentId}`
  const assignedPaid = sponsor.sponsorSubscriptions.some(
    (subscription) =>
      isPaidSubscription(subscription) &&
      subscription.metadata?.[assignmentKey] === studentId,
  )

  return approved && assignedPaid
}

export function hasPaidAccess({
  studentId,
  ownSubscriptions,
  sponsor,
  schools,
}: PaidAccessInput): PaidAccess {
  if (ownSubscriptions.some(isPaidSubscription))
    return { paid: true, source: 'own' }
  if (hasSponsorAccess(studentId, sponsor))
    return { paid: true, source: 'sponsor' }

  const school = (schools ?? []).find(isActiveSchool)
  if (school)
    return { paid: true, source: 'school', schoolName: school.schoolName ?? '' }

  return { paid: false, source: null }
}
